/**
 * Listening to a child, on their own device.
 *
 * One interface, so lesson code never touches a browser API
 * ([D-004](../../../docs/DECISIONS.md#d-004)), and one adapter for the Web
 * Speech API. Everything here is about a single rule:
 *
 * **The audio never leaves the device, so it must be provably on-device or not
 * happen at all.**
 *
 * That rule is sharper than it sounds. The Web Speech API does *not* mean
 * on-device: in Chrome it has always streamed the microphone to Google's
 * servers, which for this app would mean shipping recordings of children to a
 * third party — the exact thing [D-004](../../../docs/DECISIONS.md#d-004)
 * refused, and it would not even be visible from the code that "speaking now
 * works". So this asks the browser for an explicit on-device guarantee and
 * treats *anything else*, including a browser that cannot answer the question,
 * as having no microphone.
 *
 * **It fails closed**, which is the point. When the guarantee is absent the app
 * behaves exactly as it did before any of this existed: every speaking exercise
 * falls back to the path its author wrote for a child with no microphone, which
 * is already the common case and already works.
 */

import { recognitionTag } from './language.js';

/** What the device heard, as text. Never audio ([D-011](../../../docs/DECISIONS.md#d-011)). */
export interface SpeechRecognizer {
  /**
   * Listen once and answer with the transcript.
   *
   * Resolves with `''` when nothing was heard, which is a real answer and not
   * an error: a child who said nothing has told us nothing, and the scorer
   * treats it that way. Rejects only when the microphone was refused or the
   * recogniser broke — both of which put the lesson back on the no-mic path
   * rather than ending it.
   */
  listen(): Promise<string>;
  /** Stop listening now. Called when a child leaves the exercise mid-phrase. */
  stop(): void;
}

/** The shape of the bits of the Web Speech API this uses. */
interface RecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  processLocally?: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechResultLike) => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
  onend: (() => void) | null;
}

interface SpeechResultLike {
  readonly results: ArrayLike<ArrayLike<{ readonly transcript: string }>>;
}

interface RecognitionConstructor {
  new (): RecognitionLike;
  /**
   * Chrome's on-device availability check. Its absence is what tells us a
   * browser cannot promise the audio stays put.
   */
  available?: (options: {
    langs: readonly string[];
    processLocally: boolean;
  }) => Promise<string>;
  /** Fetch the on-device model. Needs a real tap behind it. */
  install?: (options: { langs: readonly string[] }) => Promise<boolean>;
}

/**
 * What this device can do about listening, as a grown-up would put it.
 *
 * - `unsupported` — this browser will not promise to keep the audio here, so
 *   CapyTalk will not ask for the microphone at all.
 * - `downloadable` — it can, once the on-device model has been fetched. That
 *   is a real download onto someone's device, so it is offered and never done
 *   quietly ([D-096](../../../docs/DECISIONS.md#d-096)).
 * - `downloading` — already on its way, probably from another tab or another
 *   app.
 * - `available` — ready now.
 */
export type SpeechState = 'unsupported' | 'downloadable' | 'downloading' | 'available';

const constructorFor = (): RecognitionConstructor | null => {
  const scope = globalThis as unknown as {
    SpeechRecognition?: RecognitionConstructor;
    webkitSpeechRecognition?: RecognitionConstructor;
  };
  return scope.SpeechRecognition ?? scope.webkitSpeechRecognition ?? null;
};

/**
 * Whether this device can listen **without sending the audio anywhere**.
 *
 * `available({ processLocally: true })` is the only way a browser currently
 * says yes to that question, so a browser without it is a no. That is stricter
 * than it needs to be for Safari, whose recognition has been on-device for
 * years without advertising it — and it is the right way to be wrong. Being
 * too cautious costs a feature that already has a working fallback; being too
 * trusting costs a child's voice going somewhere nobody agreed to.
 */
export async function speechState(lang = recognitionTag()): Promise<SpeechState> {
  const Recognition = constructorFor();
  if (Recognition?.available === undefined) return 'unsupported';
  try {
    const answer = await Recognition.available({
      langs: [lang],
      processLocally: true,
    });
    if (answer === 'available') return 'available';
    if (answer === 'downloading') return 'downloading';
    // Anything else — `unavailable`, or a word this app has never heard of — is
    // "no". A browser inventing a new answer must not be read as a yes.
    return answer === 'downloadable' ? 'downloadable' : 'unsupported';
  } catch {
    return 'unsupported';
  }
}

/**
 * Whether a lesson may listen right now.
 *
 * Only `available` counts. `downloadable` is a browser saying *it could, if
 * somebody fetched a model* — which is not a reason to hold up a lesson, and
 * certainly not a reason to fall back to the cloud.
 */
export async function canListenPrivately(lang = recognitionTag()): Promise<boolean> {
  return (await speechState(lang)) === 'available';
}

/**
 * Fetch the on-device model, because a grown-up asked for it.
 *
 * Never called on a child's behalf and never at launch: it is a download onto
 * someone's device and it needs a real tap behind it — Chrome requires user
 * activation, and so does common decency.
 */
export async function installSpeech(lang = recognitionTag()): Promise<boolean> {
  const Recognition = constructorFor();
  if (Recognition?.install === undefined) return false;
  try {
    return await Recognition.install({ langs: [lang] });
  } catch {
    return false;
  }
}

/** How long a child is given to say it before the recogniser is asked to stop. */
export const LISTEN_MS = 6_000;

/**
 * A recogniser for this device, or null when the audio could not be kept on it.
 *
 * Null is the ordinary answer on most browsers today, and every caller has to
 * treat it as "this child has no microphone" — which is a state the lesson
 * runner already handles, because a refused permission looks the same.
 */
export function recognizer(lang = recognitionTag()): SpeechRecognizer | null {
  const Recognition = constructorFor();
  if (Recognition === null) return null;

  let live: RecognitionLike | null = null;

  return {
    listen: () =>
      new Promise<string>((resolve, reject) => {
        const recognition = new Recognition();
        live = recognition;
        recognition.lang = lang;
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;
        // Belt as well as braces: `canListenPrivately` has already established
        // that on-device is possible, and this is what asks for it.
        recognition.processLocally = true;

        let heard = '';
        let settled = false;
        const finish = (): void => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          live = null;
          resolve(heard);
        };

        const timer = setTimeout(() => {
          // Stop rather than abort, so whatever was said before the timeout
          // still counts. A child who took their time has still answered.
          recognition.stop();
        }, LISTEN_MS);

        recognition.onresult = (event) => {
          heard = event.results[0]?.[0]?.transcript ?? '';
        };
        recognition.onend = finish;
        recognition.onerror = (event) => {
          if (settled) return;
          // "no-speech" is silence, which is an answer rather than a failure.
          if (event.error === 'no-speech') {
            finish();
            return;
          }
          settled = true;
          clearTimeout(timer);
          live = null;
          reject(new Error(event.error ?? 'speech-failed'));
        };

        try {
          recognition.start();
        } catch (error) {
          settled = true;
          clearTimeout(timer);
          live = null;
          reject(error instanceof Error ? error : new Error('speech-failed'));
        }
      }),

    stop: () => {
      live?.abort();
      live = null;
    },
  };
}
